import { useTranslation } from 'react-i18next';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { OPERATOR_ACTION_KINDS } from '@/lib/operator-activity/types';
import { fmtDateTime } from '@/lib/documents/format';
import { localized, num, p50p95, pct, priceText, usd } from '@/lib/reports/format';
import type { DisruptionDossier, ReportKnown, ReportMetricKey } from '@/lib/reports/types';
import { useDossier, useDossierDownload } from '@/lib/reports/use-reports';
import { DocumentSheetDialog, DocumentStamp, ExerciseBanner } from './document-sheet-dialog';

// The FICHA DE DISRUPCIÓN (#823, SKY PoC §8): the per-disruption dossier Nexa hands
// over after a rehearsal — the §8 metrics with their definitions, the totals the
// hotel headline and the reconciliation CSV share (`reconciledWithSummary`), the hotels
// and the operators' work. Printable A4 through the same shell as the supplier
// documents (#809); CSV beside it. Unknown reads are said so, never shown as zero.
// Every amount is the airline's price (commercial-contract M3, converted by the BFF);
// without a price card it reads «No disponible» and a notice says why.

const METRIC_ORDER: readonly ReportMetricKey[] = ['passengers', 'pricePerPassenger', 'allocationAccuracy', 'manualWork', 'providerWait', 'effectiveTime'];

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <tr className="border-b border-neutral-200">
      <td className="py-0.5 pr-3 text-neutral-600">{label}</td>
      <td className={`py-0.5 text-right tabular-nums ${mono ? 'font-mono text-xs' : ''}`}>{value}</td>
    </tr>
  );
}

function MissingReads({ known }: { known: ReportKnown }) {
  const { t } = useTranslation('admin');
  const missing = (Object.keys(known) as (keyof ReportKnown)[]).filter((k) => !known[k]).map((k) => t(`reports.sources.${k}`));
  if (missing.length === 0) return null;
  return (
    <p className="mb-3 flex items-center gap-2 border border-amber-500 bg-amber-50 px-3 py-1.5 text-xs text-amber-900" data-testid="dossier-unknown">
      <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden />
      {t('reports.unknownHint', { sources: missing.join(', ') })}
    </p>
  );
}

export function DisruptionDossierSheet({ dossier }: { dossier: DisruptionDossier }) {
  const { t, i18n } = useTranslation('admin');
  const lang = i18n.language;
  const m = dossier.metrics;
  const d = dossier.header.disruption;
  const price = (v: number | null) => priceText(v, dossier.priced, t('operateCancel:spend.unpriced'));
  const values: Record<ReportMetricKey, Array<[string, string]>> = {
    disruptions: [],
    passengers: [
      [t('reports.dossier.rows.passengers'), String(m.passengers)],
      [t('reports.dossier.rows.attended'), String(m.attended)],
      [t('reports.dossier.rows.excluded'), String(m.excluded)],
    ],
    pricePerPassenger: [
      [t('reports.dossier.rows.hotelUsd'), t('reports.dossier.priceValue', { total: price(m.price.hotelUsd), perPax: price(m.price.perPassenger.hotelUsd) })],
      [t('reports.dossier.rows.transportUsd'), t('reports.dossier.priceValue', { total: price(m.price.transportUsd), perPax: price(m.price.perPassenger.transportUsd) })],
      [t('reports.dossier.rows.mealUsd'), t('reports.dossier.priceValue', { total: price(m.price.mealUsd), perPax: price(m.price.perPassenger.mealUsd) })],
      [t('reports.dossier.rows.totalUsd'), t('reports.dossier.priceValue', { total: price(m.price.totalUsd), perPax: price(m.price.perPassenger.totalUsd) })],
    ],
    allocationAccuracy: [
      [t('reports.dossier.rows.accuracy'), pct(m.accuracy.accuratePct)],
      [t('reports.dossier.rows.withDifferences'), String(m.accuracy.withDifferences)],
      ...Object.entries(m.accuracy.byDifference).map(([code, n]): [string, string] => [code, String(n)]),
    ],
    manualWork: [
      [t('reports.dossier.rows.actions'), String(m.manualWork.actions)],
      [t('reports.dossier.rows.operators'), String(m.manualWork.operators)],
      [t('reports.dossier.rows.actionsPerPax'), num(m.manualWork.actionsPerPassenger, 2)],
      ...OPERATOR_ACTION_KINDS.filter((k) => m.manualWork.byAction[k] > 0).map((k): [string, string] => [t(`reports.actions.${k}`), String(m.manualWork.byAction[k])]),
      [t('reports.dossier.rows.unattributed'), String(m.manualWork.unattributed)],
      [t('reports.dossier.rows.passengerCheckIns'), String(m.manualWork.passengerDriven.checkIn)],
    ],
    providerWait: m.providerWait.known
      ? [
          [t('reports.dossier.rows.waitHotel'), `${p50p95(m.providerWait.hotel, 's')} (n=${m.providerWait.hotel.count})`],
          [t('reports.dossier.rows.waitTransport'), `${p50p95(m.providerWait.transport, 's')} (n=${m.providerWait.transport.count})`],
        ]
      : [[t('reports.dossier.rows.waitHotel'), t('reports.cards.providerWait.unknown')]],
    effectiveTime: [
      [t('reports.dossier.rows.startAt'), fmtDateTime(m.effectiveTime.startAt, lang)],
      [t('reports.dossier.rows.checkedIn'), `${m.effectiveTime.checkedIn} · ${pct(m.effectiveTime.coveragePct)}`],
      [t('reports.dossier.rows.minutesToCheckIn'), p50p95(m.effectiveTime.minutesToCheckIn, 'min')],
      [t('reports.dossier.rows.lastPassenger'), t('reports.dossier.lastPassengerValue', { at: fmtDateTime(m.effectiveTime.lastPassengerAt, lang), minutes: num(m.effectiveTime.lastPassengerMinutes) })],
      [t('reports.dossier.rows.marks'), `${fmtDateTime(m.effectiveTime.firstMarkAt, lang)} → ${fmtDateTime(m.effectiveTime.lastMarkAt, lang)}`],
    ],
  };
  const totals = dossier.totals;
  return (
    <article className="mx-auto max-w-3xl bg-white p-8 text-black shadow-sm print:p-0 print:shadow-none" data-print-sheet data-print-format="a4" data-testid="dossier-sheet">
      <ExerciseBanner exerciseLabel={dossier.header.exerciseLabel ?? undefined} />
      <header className="border-b-2 border-black pb-3">
        <p className="text-xs uppercase tracking-[0.3em] text-neutral-500">{t('reports.dossier.title')}</p>
        <h1 className="mt-1 text-2xl font-bold" data-testid="dossier-title">
          {d.title}
        </h1>
        <dl className="mt-2 grid grid-cols-2 gap-x-6 gap-y-1 text-sm sm:grid-cols-4">
          <div>
            <dt className="text-[10px] uppercase tracking-wide text-neutral-500">{t('reports.table.declared')}</dt>
            <dd>{fmtDateTime(d.detectedAt || null, lang)}</dd>
          </div>
          <div>
            <dt className="text-[10px] uppercase tracking-wide text-neutral-500">{t('reports.table.status')}</dt>
            <dd>{t(`reports.status.${d.status}`, { defaultValue: d.status })}</dd>
          </div>
          <div>
            <dt className="text-[10px] uppercase tracking-wide text-neutral-500">{t('reports.dossier.flights')}</dt>
            <dd>{d.flights.length ? d.flights.join(', ') : '—'}</dd>
          </div>
          <div>
            <dt className="text-[10px] uppercase tracking-wide text-neutral-500">{t('reports.dossier.urn')}</dt>
            <dd className="font-mono text-xs">{d.urn}</dd>
          </div>
        </dl>
      </header>

      <div className="mt-4">
        <MissingReads known={dossier.known} />
        {!dossier.priced ? (
          <p className="mb-3 flex items-center gap-2 border border-amber-500 bg-amber-50 px-3 py-1.5 text-xs text-amber-900" role="status" data-testid="dossier-unpriced">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden />
            {t('operateCancel:spend.unpricedHint')}
          </p>
        ) : null}
      </div>

      <section className="mt-2" data-testid="dossier-metrics">
        {METRIC_ORDER.map((key) => (
          <div key={key} className="mt-4 break-inside-avoid" data-testid={`dossier-metric-${key}`}>
            <h2 className="text-sm font-bold uppercase tracking-wide">{localized(dossier.definitions[key].label, lang)}</h2>
            <p className="mt-0.5 text-[11px] leading-snug text-neutral-600" data-testid={`dossier-definition-${key}`}>
              {localized(dossier.definitions[key].definition, lang)}
            </p>
            <table className="mt-1 w-full text-sm">
              <tbody>
                {values[key].map(([label, value]) => (
                  <Row key={label} label={label} value={value} />
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </section>

      <section className="mt-6 break-inside-avoid" data-testid="dossier-totals">
        <h2 className="text-sm font-bold uppercase tracking-wide">{t('reports.dossier.totalsTitle')}</h2>
        <p className="mt-0.5 flex items-center gap-1 text-[11px] text-neutral-600">
          {dossier.reconciledWithSummary === true ? <CheckCircle2 className="h-3 w-3 text-emerald-700" aria-hidden /> : dossier.reconciledWithSummary === false ? <AlertTriangle className="h-3 w-3 text-amber-700" aria-hidden /> : null}
          {t(`reports.reconciled.${dossier.reconciledWithSummary === null ? 'unknown' : dossier.reconciledWithSummary ? 'yes' : 'no'}`, { headline: usd(dossier.summaryHotelSpendUsd) })}
        </p>
        {totals ? (
          <table className="mt-1 w-full text-sm">
            <tbody>
              <Row label={t('reports.dossier.totals.hotelExpected')} value={`${totals.hotel.expected} · ${totals.hotel.confirmed} ${t('reports.dossier.totals.confirmed')} · ${totals.hotel.held} ${t('reports.dossier.totals.held')} · ${totals.hotel.failed} ${t('reports.dossier.totals.failed')} · ${totals.hotel.selfManaged} ${t('reports.dossier.totals.selfManaged')}`} />
              <Row label={t('reports.dossier.totals.hotelPrice')} value={`${price(totals.hotel.priceUsd)} + ${price(totals.hotel.estimatedUsd)} ${t('reports.dossier.totals.estimated')}`} />
              <Row label={t('reports.dossier.totals.transport')} value={`${totals.transport.expected} · ${totals.transport.withOutbound} ${t('reports.dossier.totals.outbound')} · ${totals.transport.withInbound} ${t('reports.dossier.totals.inbound')} · ${price(totals.transport.priceUsd)}`} />
              <Row label={t('reports.dossier.totals.meal')} value={`${totals.meal.expected} · ${totals.meal.issued} ${t('reports.dossier.totals.issued')} · ${totals.meal.redeemed} ${t('reports.dossier.totals.redeemed')} · ${price(totals.meal.issuedUsd)} + ${price(totals.meal.toppedUpUsd)} top-up`} />
              <Row label={t('reports.dossier.totals.notifications')} value={`${totals.notifications.delivered} ${t('reports.dossier.totals.delivered')} · ${totals.notifications.failed} ${t('reports.dossier.totals.failedN')} · ${totals.notifications.unknown} ${t('reports.dossier.totals.unknownN')}`} />
            </tbody>
          </table>
        ) : (
          <p className="mt-1 text-sm text-neutral-600" data-testid="dossier-no-totals">
            {t('reports.dossier.noTotals')}
          </p>
        )}
      </section>

      <section className="mt-6 break-inside-avoid" data-testid="dossier-hotels">
        <h2 className="text-sm font-bold uppercase tracking-wide">{t('reports.dossier.hotelsTitle')}</h2>
        {dossier.hotels.length === 0 ? (
          <p className="mt-1 text-sm text-neutral-600">{t('reports.dossier.noHotels')}</p>
        ) : (
          <table className="mt-1 w-full text-sm">
            <thead>
              <tr className="border-b border-black text-left text-[10px] uppercase tracking-wide text-neutral-600">
                <th className="py-0.5 pr-2">{t('reports.dossier.hotel.name')}</th>
                <th className="py-0.5 pr-2">{t('reports.dossier.hotel.vendor')}</th>
                <th className="py-0.5 pr-2 text-right">{t('reports.dossier.hotel.passengers')}</th>
                <th className="py-0.5 pr-2 text-right">{t('reports.dossier.hotel.nights')}</th>
                <th className="py-0.5 text-right">{t('reports.dossier.hotel.price')}</th>
              </tr>
            </thead>
            <tbody>
              {dossier.hotels.map((h) => (
                <tr key={`${h.hotelName}|${h.vendor}`} className="border-b border-neutral-200">
                  <td className="py-0.5 pr-2">{h.hotelName ?? '—'}</td>
                  <td className="py-0.5 pr-2">{h.vendor ?? '—'}</td>
                  <td className="py-0.5 pr-2 text-right tabular-nums">{h.passengers}</td>
                  <td className="py-0.5 pr-2 text-right tabular-nums">{h.nights ?? '—'}</td>
                  <td className="py-0.5 text-right tabular-nums">{price(h.priceUsd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="mt-6 break-inside-avoid" data-testid="dossier-operators">
        <h2 className="text-sm font-bold uppercase tracking-wide">{t('reports.operators.title')}</h2>
        {dossier.operators.length === 0 ? (
          <p className="mt-1 text-sm text-neutral-600">{t('reports.operators.empty')}</p>
        ) : (
          <table className="mt-1 w-full text-sm">
            <thead>
              <tr className="border-b border-black text-left text-[10px] uppercase tracking-wide text-neutral-600">
                <th className="py-0.5 pr-2">{t('reports.operators.operator')}</th>
                <th className="py-0.5 pr-2 text-right">{t('reports.operators.total')}</th>
                <th className="py-0.5 pr-2 text-right">{t('reports.operators.passengers')}</th>
                <th className="py-0.5 pr-2">{t('reports.operators.first')}</th>
                <th className="py-0.5">{t('reports.operators.last')}</th>
              </tr>
            </thead>
            <tbody>
              {dossier.operators.map((o) => (
                <tr key={o.operatorUrn} className="border-b border-neutral-200">
                  <td className="py-0.5 pr-2">{o.name ?? o.operatorUrn.replace(/^urn:user:/, '')}</td>
                  <td className="py-0.5 pr-2 text-right tabular-nums">{o.total}</td>
                  <td className="py-0.5 pr-2 text-right tabular-nums">{o.passengers}</td>
                  <td className="py-0.5 pr-2 text-xs">{fmtDateTime(o.firstAt, lang)}</td>
                  <td className="py-0.5 text-xs">{fmtDateTime(o.lastAt, lang)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <DocumentStamp generatedAt={dossier.header.generatedAt} generatedBy={dossier.header.generatedBy} exerciseLabel={dossier.header.exerciseLabel ?? undefined} />
    </article>
  );
}

export function DisruptionDossierDialog({ urn, title, onClose }: { urn: string; title: string; onClose: () => void }) {
  const { t } = useTranslation('admin');
  const query = useDossier(urn);
  const download = useDossierDownload(urn);
  return (
    <DocumentSheetDialog
      open
      onClose={onClose}
      title={t('reports.dossier.dialogTitle', { title: query.data?.header.disruption.title ?? title })}
      description={t('reports.dossier.dialogDescription')}
      ready={query.isSuccess}
      error={query.isError ? t('reports.dossier.error') : null}
      onDownloadCsv={() => download.mutate()}
      downloading={download.isPending}
      testId="dossier-dialog"
    >
      {query.data ? <DisruptionDossierSheet dossier={query.data} /> : null}
    </DocumentSheetDialog>
  );
}
